import { localEventDate } from '../../shared/eventTimezones';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormControl,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FemaleIcon from '@mui/icons-material/Female';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MaleIcon from '@mui/icons-material/Male';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TransgenderIcon from '@mui/icons-material/Transgender';
import RefreshIcon from '@mui/icons-material/Refresh';
import { supabase } from '../config/supabase';
import { useAuditUserId } from '../auth/AuthContext';
import { lookupLabel } from '../data/lookups';
import { availabilityStatusOptions } from '../data/options';
import type { AvailabilityRecord, AvailabilityStatus, LookupState, MatchEventRecord, TimeFormat, UserPreferences } from '../types';
import { comparePlayerNames, formatPlayerName } from '../utils/playerPreferences';
import { formatTime } from '../utils/timeFormat';
import { isDesignatedSubstitute, normalizeRosterRole, type RosterRole } from '../utils/rosterRoles';
import { loadUnreviewedAvailabilityChanges, type AvailabilityChangeAttention } from '../utils/availabilityAttention';
import { PlayerDisplayControls } from './PlayerDisplayControls';
import { SeasonTeamMultiSelect } from './SeasonTeamMultiSelect';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';
import { BreadcrumbTrail } from './ux/BreadcrumbTrail';
import { availabilityCanonicalContext, buildBreadcrumbTrail, type BreadcrumbItem } from '../utils/uxNavigation';

type AvailabilityMatrixProps = {
  lookups: LookupState;
  userPreferences: UserPreferences;
  onUserPreferencesChange: (patch: Partial<UserPreferences>) => void;
  timeFormat: TimeFormat;
  initialTeamId?: string | null;
  initialMatchId?: string | null;
  readOnly?: boolean;
  onOpenLineup?: (matchId: string, context?: { teamId?: string | null }) => void;
  onBack?: () => void;
  backLabel?: string;
  breadcrumbOrigin?: 'planning-board' | 'match-detail' | 'team-events' | 'direct';
  onBreadcrumbNavigate?: (item: BreadcrumbItem) => void;
  isWorkflowFullScreen?: boolean;
  onExitWorkflowFullScreen?: () => void;
};

type TeamOption = {
  id: string;
  team_name: string;
  season_label: string | null;
  league_configuration_id: string;
  team_gender_type?: 'mens' | 'womens' | 'mixed' | null;
};

type RosterPlayer = {
  id: string;
  first_name: string;
  last_name: string;
  sex: 'male' | 'female' | string | null;
  estimated_dynamic_rating: number | null;
  player_tier: string | null;
  roster_role: RosterRole | string | null;
};

type RosterRow = {
  player_id: string;
  player_tier: string | null;
  roster_role: RosterRole | string | null;
};

type RequiredSlotsRow = {
  league_configuration_id: string;
  required_player_count: number;
};

type Snack = {
  message: string;
  severity: 'success' | 'error' | 'info';
};

type AvailabilityFilter = 'all' | AvailabilityStatus;
type GenderFilter = 'all' | 'male' | 'female';
type AvailabilitySort = 'no_response' | 'first_name' | 'last_name' | 'availability' | 'gender' | 'last_updated';
type AvailabilityMatrixViewState = {
  teamId: string;
  selectedMatchId: string;
  availabilityFilter: AvailabilityFilter;
  genderFilter: GenderFilter;
  search: string;
  availabilitySort: AvailabilitySort;
  groupByGender: boolean;
};

const statusStyles: Record<AvailabilityStatus, { bg: string; border: string; text: string }> = {
  available: { bg: '#dff3e3', border: '#7fbd8a', text: '#174c22' },
  maybe: { bg: '#fff4cf', border: '#e5b84f', text: '#6b4b00' },
  unknown: { bg: '#eeeeee', border: '#bdbdbd', text: '#424242' },
  not_available: { bg: '#fde1df', border: '#e58b84', text: '#7b1f1a' },
};

const availabilityFilterOptions: Array<{ value: AvailabilityFilter; label: string }> = [
  { value: 'all', label: 'All Players' },
  { value: 'available', label: 'Available' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'not_available', label: 'Unavailable' },
  { value: 'unknown', label: 'No Response' },
];

const genderFilterOptions: Array<{ value: GenderFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'male', label: 'Men' },
  { value: 'female', label: 'Women' },
];

function matchHeading(match: MatchEventRecord): string {
  const date = formatMatchDate(localEventDate(match));
  const opponent = match.opponent_name || 'TBD';
  return `${date} vs ${opponent}`;
}

function matchCrumbLabel(match: MatchEventRecord | null): string {
  if (!match) return 'Current Match';
  return `vs ${match.opponent_name || 'TBD'}`;
}

function availabilityLabel(status: AvailabilityStatus): string {
  return availabilityStatusOptions.find((option) => option.value === status)?.label || status;
}

function formatMatchDate(value: string | null | undefined): string {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function isUpcomingPlayable(match: MatchEventRecord): boolean {
  return ['Scheduled', 'Postponed', 'Rescheduled', 'Draft'].includes(match.match_status || 'Draft') && match.match_date >= localTodayIso();
}

function genderLabel(sex: string | null | undefined): string {
  if (sex === 'male') return 'Man';
  if (sex === 'female') return 'Woman';
  return 'Gender not specified';
}

function tierLabel(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusSortRank(status: AvailabilityStatus): number {
  if (status === 'unknown') return 0;
  if (status === 'maybe') return 1;
  if (status === 'available') return 2;
  return 3;
}

function localTodayIso(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function groupMeStatusLabel(status: string): string {
  if (status === 'available') return 'Available';
  if (status === 'maybe') return 'Maybe';
  if (status === 'not_available') return 'Not Available';
  return 'No Response';
}

function groupMeUpdateTimestamp(value: string, timeFormat: TimeFormat): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const dateLabel = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const timeLabel = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: timeFormat === '12h',
  });
  return `${dateLabel}, ${timeLabel}`;
}

function groupMeUpdateMatchLabel(update: AvailabilityChangeAttention, timeFormat: TimeFormat): string {
  const teamName = update.team_name || '';
  const opponentName = update.opponent_name || 'TBD';
  const matchDate = update.match_date ? formatMatchDate(localEventDate(update)) : '';
  const matchTime = formatTime(update.match_time || null, timeFormat, update);
  const matchName = `${teamName ? `${teamName} vs ` : 'vs '}${opponentName}`;
  return [matchName, [matchDate, matchTime].filter(Boolean).join(' · ')].filter(Boolean).join(' on ');
}

export function AvailabilityMatrix({
  lookups,
  userPreferences,
  onUserPreferencesChange,
  timeFormat,
  initialTeamId,
  initialMatchId,
  readOnly = false,
  onOpenLineup,
  onBack,
  backLabel = 'Back',
  breadcrumbOrigin = 'direct',
  onBreadcrumbNavigate,
  isWorkflowFullScreen = false,
  onExitWorkflowFullScreen,
}: AvailabilityMatrixProps) {
  const auditUserId = useAuditUserId();
  const defaultViewState = useMemo<AvailabilityMatrixViewState>(() => ({
    teamId: '',
    selectedMatchId: '',
    availabilityFilter: 'all',
    genderFilter: 'all',
    search: '',
    availabilitySort: 'no_response',
    groupByGender: Boolean(userPreferences.groupPlayersBySex),
  }), [userPreferences.groupPlayersBySex]);
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState({
    userId: auditUserId,
    routeKey: 'availability',
    defaultState: defaultViewState,
  });
  const [teamId, setTeamId] = useState(initialTeamId || savedViewState.teamId);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState(initialMatchId || savedViewState.selectedMatchId);
  const [dateFrom, setDateFrom] = useState(localTodayIso);
  const [players, setPlayers] = useState<RosterPlayer[]>([]);
  const [matches, setMatches] = useState<MatchEventRecord[]>([]);
  const [matchesLoadedForTeamId, setMatchesLoadedForTeamId] = useState<string | null>(null);
  const [records, setRecords] = useState<AvailabilityRecord[]>([]);
  const [requiredSlotsByConfig, setRequiredSlotsByConfig] = useState<Record<string, number>>({});
  const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilter>(savedViewState.availabilityFilter);
  const [genderFilter, setGenderFilter] = useState<GenderFilter>(savedViewState.genderFilter);
  const [search, setSearch] = useState(savedViewState.search);
  const [availabilitySort, setAvailabilitySort] = useState<AvailabilitySort>(savedViewState.availabilitySort);
  const [groupByGender, setGroupByGender] = useState(savedViewState.groupByGender);
  const [bulkAnchorEl, setBulkAnchorEl] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [snack, setSnack] = useState<Snack | null>(null);
  const [groupMeUpdates, setGroupMeUpdates] = useState<AvailabilityChangeAttention[]>([]);
  const [groupMeUpdatesLoading, setGroupMeUpdatesLoading] = useState(true);
  const restoredViewStateKey = useRef('');
  const resolvedInitialMatchKey = useRef('');
  const matrixRequestId = useRef(0);
  const [viewStateReady, setViewStateReady] = useState(false);

  usePersistentScrollRestoration({
    userId: auditUserId,
    routeKey: 'availability',
    ready: !loading && !groupMeUpdatesLoading,
  });

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setTeamId(initialTeamId || savedViewState.teamId);
    setSelectedMatchId(initialMatchId || savedViewState.selectedMatchId);
    setAvailabilityFilter(savedViewState.availabilityFilter);
    setGenderFilter(savedViewState.genderFilter);
    setSearch(savedViewState.search);
    setAvailabilitySort(savedViewState.availabilitySort);
    setGroupByGender(savedViewState.groupByGender);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, initialMatchId, initialTeamId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({ teamId, selectedMatchId, availabilityFilter, genderFilter, search, availabilitySort, groupByGender });
  }, [availabilityFilter, availabilitySort, genderFilter, groupByGender, search, selectedMatchId, setSavedViewState, teamId, viewStateKey, viewStateReady]);

  useEffect(() => {
    setGroupMeUpdatesLoading(true);
    void loadUnreviewedAvailabilityChanges()
      .then((updates) => {
        setGroupMeUpdates(updates);
      })
      .catch(() => {
        setSnack({ severity: 'error', message: 'Unable to load recent GroupMe availability updates.' });
      })
      .finally(() => setGroupMeUpdatesLoading(false));
  }, []);

  useEffect(() => {
    if (!initialTeamId || !teamsLoaded) return;
    const requestedTeam = teams.find((team) => team.id === initialTeamId);
    if (!requestedTeam) return;
    setTeamId(requestedTeam.id);
  }, [initialTeamId, teams, teamsLoaded]);

  useEffect(() => {
    if (!initialMatchId || matchesLoadedForTeamId !== teamId) return;
    if (matches.some((match) => match.id === initialMatchId)) setSelectedMatchId(initialMatchId);
  }, [initialMatchId, matches, matchesLoadedForTeamId, teamId]);

  useEffect(() => {
    if (!viewStateReady || !teamsLoaded || teams.length === 0) return;
    if (!teamId || !teams.some((team) => team.id === teamId)) {
      const requestedTeam = initialTeamId ? teams.find((team) => team.id === initialTeamId) : null;
      setTeamId(requestedTeam?.id || teams[0].id);
    }
  }, [initialTeamId, teamId, teams, teamsLoaded, viewStateReady]);

  const recordByCell = useMemo(() => {
    const map = new Map<string, AvailabilityRecord>();
    records.forEach((record) => map.set(`${record.match_event_id}:${record.player_id}`, record));
    return map;
  }, [records]);

  const matchCounts = useMemo(() => {
    return matches.map((match) => {
      let availableMaybe = 0;
      let unknown = 0;
      players.forEach((player) => {
        const status = recordByCell.get(`${match.id}:${player.id}`)?.availability_status || 'unknown';
        if (status === 'available' || status === 'maybe') availableMaybe += 1;
        if (status === 'unknown') unknown += 1;
      });
      const requiredSlots = requiredSlotsByConfig[match.league_configuration_id] || 0;
      return { matchId: match.id, availableMaybe, unknown, requiredSlots, isLow: requiredSlots > 0 && availableMaybe < requiredSlots };
    });
  }, [matches, players, recordByCell, requiredSlotsByConfig]);

  const summary = useMemo(() => {
    const matchesWithUnknown = matchCounts.filter((item) => item.unknown > 0).length;
    const matchesWithLowAvailableMaybe = matchCounts.filter((item) => item.isLow).length;
    return { matchesWithUnknown, matchesWithLowAvailableMaybe };
  }, [matchCounts]);

  const loadTeams = useCallback(async () => {
    setTeamsLoaded(false);
    const { data, error } = await supabase
      .from('teams')
      .select('id, team_name, season_label, league_configuration_id, team_gender_type')
      .is('deleted_at', null)
      .eq('is_active', true)
      .order('team_name');

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      setTeamsLoaded(true);
      return;
    }
    setTeams((data || []) as unknown as TeamOption[]);
    setTeamsLoaded(true);
  }, []);

  const loadRequiredSlots = useCallback(async () => {
    const { data, error } = await supabase
      .from('league_configuration_positions')
      .select('league_configuration_id, required_player_count')
      .is('deleted_at', null)
      .eq('is_active', true);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    const totals = ((data || []) as unknown as RequiredSlotsRow[]).reduce<Record<string, number>>((acc, row) => {
      acc[row.league_configuration_id] = (acc[row.league_configuration_id] || 0) + Number(row.required_player_count || 0);
      return acc;
    }, {});
    setRequiredSlotsByConfig(totals);
  }, []);

  const loadMatrix = useCallback(async () => {
    const requestId = ++matrixRequestId.current;
    if (!teamId) {
      setPlayers([]);
      setMatches([]);
      setRecords([]);
      setMatchesLoadedForTeamId(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setMatchesLoadedForTeamId(null);

    let effectiveDateFrom = dateFrom;
    const initialMatchKey = initialMatchId ? `${teamId}:${initialMatchId}` : '';
    if (initialMatchKey && resolvedInitialMatchKey.current !== initialMatchKey) {
      const linkedMatch = await supabase
        .from('match_events')
        .select('match_date')
        .eq('team_id', teamId)
        .eq('id', initialMatchId)
        .is('deleted_at', null)
        .maybeSingle();
      if (requestId !== matrixRequestId.current) return;
      if (linkedMatch.error) {
        setLoading(false);
        setSnack({ severity: 'error', message: linkedMatch.error.message });
        return;
      }
      resolvedInitialMatchKey.current = initialMatchKey;
      if (linkedMatch.data?.match_date && linkedMatch.data.match_date < dateFrom) {
        effectiveDateFrom = linkedMatch.data.match_date;
        setDateFrom(effectiveDateFrom);
      }
    }

    const rosterResult = await supabase
      .from('team_rosters')
      .select('player_id, player_tier, roster_role')
      .eq('team_id', teamId)
      .eq('is_active', true)
      .is('deleted_at', null);
    if (requestId !== matrixRequestId.current) return;

    if (rosterResult.error) {
      setLoading(false);
      setSnack({ severity: 'error', message: rosterResult.error.message });
      return;
    }

    const rosterRows = (rosterResult.data || []) as unknown as RosterRow[];
    const playerIds = rosterRows.map((row) => row.player_id);

    const playersPromise =
      playerIds.length > 0
        ? supabase
            .from('players')
            .select('id, first_name, last_name, sex, estimated_dynamic_rating')
            .in('id', playerIds)
            .is('deleted_at', null)
            .eq('is_active', true)
        : Promise.resolve({ data: [], error: null });

    let matchQuery = supabase
      .from('match_events')
      .select('*')
      .eq('team_id', teamId)
      .is('deleted_at', null)
      .order('match_date', { ascending: true })
      .order('match_time', { ascending: true });
    if (effectiveDateFrom) matchQuery = matchQuery.gte('match_date', effectiveDateFrom);

    const [playersResult, matchesResult] = await Promise.all([playersPromise, matchQuery]);
    if (requestId !== matrixRequestId.current) return;

    if (playersResult.error || matchesResult.error) {
      setLoading(false);
      setSnack({ severity: 'error', message: playersResult.error?.message || matchesResult.error?.message || 'Unable to load availability matrix.' });
      return;
    }

    const rosterByPlayerId = new Map(rosterRows.map((row) => [row.player_id, row]));
    const loadedPlayers = ((playersResult.data || []) as unknown as RosterPlayer[])
      .map((player) => ({
        ...player,
        player_tier: rosterByPlayerId.get(player.id)?.player_tier || null,
        roster_role: normalizeRosterRole(rosterByPlayerId.get(player.id)?.roster_role),
      }))
      .sort((a, b) => comparePlayerNames(a, b, userPreferences));
    const loadedMatches = (matchesResult.data || []) as unknown as MatchEventRecord[];

    if (loadedMatches.length > 0) {
      const initResults = await Promise.all(
        loadedMatches.map((match) => supabase.rpc('initialize_availability_for_match', { p_match_event_id: match.id })),
      );
      if (requestId !== matrixRequestId.current) return;
      const initError = initResults.find((result) => result.error)?.error;
      if (initError) {
        setLoading(false);
        setSnack({ severity: 'error', message: initError.message });
        return;
      }
    }

    const matchIds = loadedMatches.map((match) => match.id);
    const availabilityResult =
      matchIds.length > 0 && loadedPlayers.length > 0
        ? await supabase
            .from('availability_records')
            .select('*')
            .in('match_event_id', matchIds)
            .in('player_id', loadedPlayers.map((player) => player.id))
            .is('deleted_at', null)
        : { data: [], error: null };
    if (requestId !== matrixRequestId.current) return;

    setLoading(false);

    if (availabilityResult.error) {
      setSnack({ severity: 'error', message: availabilityResult.error.message });
      setMatchesLoadedForTeamId(teamId);
      return;
    }

    setPlayers(loadedPlayers);
    setMatches(loadedMatches);
    setRecords((availabilityResult.data || []) as unknown as AvailabilityRecord[]);
    setMatchesLoadedForTeamId(teamId);
  }, [dateFrom, initialMatchId, teamId, userPreferences]);

  useEffect(() => {
    void loadTeams();
    void loadRequiredSlots();
  }, [loadRequiredSlots, loadTeams]);

  useEffect(() => {
    void loadMatrix();
  }, [loadMatrix]);

  useEffect(() => {
    if (!viewStateReady || matchesLoadedForTeamId !== teamId) return;
    if (matches.length === 0) {
      setSelectedMatchId('');
      return;
    }
    if (selectedMatchId && matches.some((match) => match.id === selectedMatchId)) return;
    const nextPlayable = matches.find(isUpcomingPlayable);
    setSelectedMatchId(nextPlayable?.id || matches[0].id);
  }, [matches, matchesLoadedForTeamId, selectedMatchId, teamId, viewStateReady]);

  const updateAvailability = async (matchId: string, playerId: string, status: AvailabilityStatus) => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    const key = `${matchId}:${playerId}`;
    setSavingKey(key);
    const { data, error } = await supabase.rpc('update_availability_status', {
      p_match_event_id: matchId,
      p_player_id: playerId,
      p_new_status: status,
      p_changed_by: auditUserId || null,
    });
    setSavingKey(null);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    const saved = data as unknown as AvailabilityRecord;
    setRecords((current) => {
      const withoutCell = current.filter((record) => !(record.match_event_id === matchId && record.player_id === playerId));
      return [...withoutCell, saved];
    });
    setSnack({ severity: 'success', message: `${availabilityLabel(status)} saved.` });
    // This is only a delivery nudge. Qualification and idempotency live in the
    // canonical database trigger, so an email failure never affects the update.
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData.session?.access_token) {
      fetch('/api/notifications/dispatch', {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionData.session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ matchEventId: matchId }),
      }).catch(() => undefined);
    }
  };

  const selectedTeam = teams.find((team) => team.id === teamId) || null;
  const selectedMatch = matches.find((match) => match.id === selectedMatchId) || null;
  const availabilityRouteContext = availabilityCanonicalContext(
    matchCrumbLabel(selectedMatch),
    breadcrumbOrigin,
    selectedMatch ? `/matches/${selectedMatch.id}` : undefined,
  );
  const selectedMatchRecords = useMemo(() => {
    if (!selectedMatch) return new Map<string, AvailabilityRecord>();
    return new Map(records.filter((record) => record.match_event_id === selectedMatch.id).map((record) => [record.player_id, record]));
  }, [records, selectedMatch]);
  const selectedMatchCounts = useMemo(() => {
    const counts = { available: 0, maybe: 0, not_available: 0, unknown: 0 };
    players.forEach((player) => {
      const status = selectedMatchRecords.get(player.id)?.availability_status || 'unknown';
      counts[status] += 1;
    });
    return counts;
  }, [players, selectedMatchRecords]);
  const isMixedTeam = selectedTeam?.team_gender_type === 'mixed' || players.some((player) => player.sex === 'male') && players.some((player) => player.sex === 'female');
  const requiredSlots = selectedMatch ? requiredSlotsByConfig[selectedMatch.league_configuration_id] || 0 : 0;
  const readyCount = selectedMatchCounts.available;
  const readinessText = requiredSlots > 0 && readyCount < requiredSlots
    ? `Need ${requiredSlots - readyCount} more available player${requiredSlots - readyCount === 1 ? '' : 's'}`
    : 'Ready to build';
  const playableMatches = matches.filter(isUpcomingPlayable);
  const currentPlayableIndex = selectedMatch ? playableMatches.findIndex((match) => match.id === selectedMatch.id) : -1;
  const previousMatch = currentPlayableIndex > 0 ? playableMatches[currentPlayableIndex - 1] || null : null;
  const nextMatch = currentPlayableIndex >= 0 ? playableMatches[currentPlayableIndex + 1] || null : playableMatches.find((match) => match.id !== selectedMatchId) || null;

  const sortedFilteredPlayers = useMemo(() => {
    const searchValue = search.trim().toLowerCase();
    const filtered = players.filter((player) => {
      const status = selectedMatchRecords.get(player.id)?.availability_status || 'unknown';
      if (availabilityFilter !== 'all' && status !== availabilityFilter) return false;
      if (isMixedTeam && genderFilter !== 'all' && player.sex !== genderFilter) return false;
      if (searchValue) {
        const haystack = `${player.first_name || ''} ${player.last_name || ''}`.toLowerCase();
        if (!haystack.includes(searchValue)) return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      const statusA = selectedMatchRecords.get(a.id)?.availability_status || 'unknown';
      const statusB = selectedMatchRecords.get(b.id)?.availability_status || 'unknown';
      if (availabilitySort === 'no_response') return statusSortRank(statusA) - statusSortRank(statusB) || comparePlayerNames(a, b, userPreferences);
      if (availabilitySort === 'availability') return availabilityLabel(statusA).localeCompare(availabilityLabel(statusB)) || comparePlayerNames(a, b, userPreferences);
      if (availabilitySort === 'gender') return String(a.sex || '').localeCompare(String(b.sex || '')) || comparePlayerNames(a, b, userPreferences);
      if (availabilitySort === 'last_updated') {
        const updatedA = selectedMatchRecords.get(a.id)?.updated_at || '';
        const updatedB = selectedMatchRecords.get(b.id)?.updated_at || '';
        return updatedB.localeCompare(updatedA) || comparePlayerNames(a, b, userPreferences);
      }
      const namePreferences = { ...userPreferences, playerSortOrder: availabilitySort === 'first_name' ? 'first_name' as const : 'last_name' as const };
      return comparePlayerNames(a, b, namePreferences);
    });
  }, [availabilityFilter, availabilitySort, genderFilter, isMixedTeam, players, search, selectedMatchRecords, userPreferences]);

  const playersByStatus = (status: AvailabilityStatus) => sortedFilteredPlayers.filter((player) => (selectedMatchRecords.get(player.id)?.availability_status || 'unknown') === status);
  const noResponsePlayers = playersByStatus('unknown');

  const updateMany = async (targetPlayers: RosterPlayer[], status: AvailabilityStatus, label: string, requiresConfirmation: boolean) => {
    if (!selectedMatch || targetPlayers.length === 0) return;
    if (requiresConfirmation && !window.confirm(`${label} will update ${targetPlayers.length} player${targetPlayers.length === 1 ? '' : 's'} and may overwrite existing responses. Continue?`)) return;
    if (!requiresConfirmation && !window.confirm(`${label} will update ${targetPlayers.length} No Response player${targetPlayers.length === 1 ? '' : 's'}. Continue?`)) return;
    setBulkAnchorEl(null);
    for (const player of targetPlayers) {
      await updateAvailability(selectedMatch.id, player.id, status);
    }
    setSnack({ severity: 'success', message: `${label} complete.` });
  };

  const clearAllResponses = async () => {
    if (!selectedMatch || players.length === 0) return;
    if (!window.confirm(`Clear All Responses will reset ${players.length} player${players.length === 1 ? '' : 's'} to No Response. Continue?`)) return;
    setBulkAnchorEl(null);
    for (const player of players) {
      await updateAvailability(selectedMatch.id, player.id, 'unknown');
    }
    setSnack({ severity: 'success', message: 'All responses cleared.' });
  };

  const renderGenderIcon = (player: RosterPlayer) => {
    if (!isMixedTeam) return null;
    const label = genderLabel(player.sex);
    const icon = player.sex === 'male' ? <MaleIcon fontSize="small" /> : player.sex === 'female' ? <FemaleIcon fontSize="small" /> : <TransgenderIcon fontSize="small" />;
    return (
      <Tooltip title={label}>
        <Box component="span" aria-label={label} sx={{ display: 'inline-flex', color: 'text.secondary', verticalAlign: 'middle' }}>
          {icon}
        </Box>
      </Tooltip>
    );
  };

  const renderStatusControls = (player: RosterPlayer) => {
    if (!selectedMatch) return null;
    const key = `${selectedMatch.id}:${player.id}`;
    const status = selectedMatchRecords.get(player.id)?.availability_status || 'unknown';
    return (
      <ToggleButtonGroup
        exclusive
        size="small"
        value={status}
        aria-label={`Availability for ${formatPlayerName(player, userPreferences)}`}
        onChange={(_, value) => {
          if (value) void updateAvailability(selectedMatch.id, player.id, value as AvailabilityStatus);
        }}
        sx={{ flexWrap: 'wrap', gap: 0.5, '& .MuiToggleButtonGroup-grouped': { border: 1, borderColor: 'divider', borderRadius: '18px !important', minHeight: 36, px: 1.25 } }}
      >
        {availabilityStatusOptions.map((option) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            disabled={readOnly || savingKey === key}
            aria-label={`Set ${formatPlayerName(player, userPreferences)} ${option.label}`}
            sx={option.value === status ? { bgcolor: statusStyles[option.value as AvailabilityStatus].bg, color: statusStyles[option.value as AvailabilityStatus].text, fontWeight: 800 } : undefined}
          >
            {option.value === 'unknown' ? 'No Response' : option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  const renderPlayerRow = (player: RosterPlayer) => {
    const record = selectedMatchRecords.get(player.id);
    const status = record?.availability_status || 'unknown';
    const style = statusStyles[status];
    return (
      <Paper key={player.id} variant="outlined" sx={{ p: 1.25, borderLeft: `5px solid ${style.border}`, bgcolor: style.bg }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              {renderGenderIcon(player)}
              <Typography variant="body2" sx={{ fontWeight: 850 }}>{formatPlayerName(player, userPreferences)}</Typography>
              <Chip size="small" label={availabilityLabel(status)} sx={{ bgcolor: 'rgba(255,255,255,0.72)', color: style.text, fontWeight: 800 }} />
              {isDesignatedSubstitute(player) && <Chip size="small" variant="outlined" color="info" label="Substitute" />}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {[tierLabel(player.player_tier), player.estimated_dynamic_rating == null ? '' : `EDR ${player.estimated_dynamic_rating.toFixed(2)}`, record?.updated_at ? `Updated ${groupMeUpdateTimestamp(record.updated_at, timeFormat)}` : 'No update yet'].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          {renderStatusControls(player)}
        </Stack>
      </Paper>
    );
  };

  const renderPlayerGroup = (groupPlayers: RosterPlayer[]) => {
    if (!groupByGender || !isMixedTeam) return <Stack spacing={1}>{groupPlayers.map(renderPlayerRow)}</Stack>;
    const men = groupPlayers.filter((player) => player.sex === 'male');
    const women = groupPlayers.filter((player) => player.sex === 'female');
    const unknown = groupPlayers.filter((player) => player.sex !== 'male' && player.sex !== 'female');
    return (
      <Stack spacing={1.25}>
        {([
          ['Men', men],
          ['Women', women],
          ['Unspecified', unknown],
        ] as Array<[string, RosterPlayer[]]>).filter(([, rows]) => rows.length > 0).map(([label, rows]) => (
          <Box key={String(label)}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>{label} ({rows.length})</Typography>
            <Stack spacing={1} sx={{ mt: 0.75 }}>{rows.map(renderPlayerRow)}</Stack>
          </Box>
        ))}
      </Stack>
    );
  };

  const renderStatusSection = (status: AvailabilityStatus, title: string) => {
    const sectionPlayers = playersByStatus(status);
    if (sectionPlayers.length === 0) return null;
    return (
      <Accordion key={status} defaultExpanded variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>{title}</Typography>
            <Chip size="small" label={sectionPlayers.length} sx={{ bgcolor: statusStyles[status].bg, color: statusStyles[status].text, fontWeight: 800 }} />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>{renderPlayerGroup(sectionPlayers)}</AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <BreadcrumbTrail
            items={buildBreadcrumbTrail(availabilityRouteContext)}
            onNavigate={onBreadcrumbNavigate}
          />
          {onBack && (
            <Button size="small" variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1 }}>
              {backLabel}
            </Button>
          )}
          {isWorkflowFullScreen && (
            <Button size="small" variant="text" startIcon={<FullscreenExitIcon />} onClick={onExitWorkflowFullScreen} sx={{ mb: 1, ml: onBack ? 1 : 0 }}>
              Exit Full Screen
            </Button>
          )}
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Player Availability
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mark roster availability across scheduled matches. Changes are saved immediately and history is preserved.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void loadMatrix()}>
          Refresh
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <SeasonTeamMultiSelect
              lookups={lookups}
              value={teamId ? [teamId] : []}
              onChange={(teamIds) => {
                setTeamId(teamIds[0] || '');
                setSelectedMatchId('');
              }}
              teams={teams.map((team) => ({ value: team.id, label: team.team_name, season_label: team.season_label }))}
              multiple={false}
            />
          </Box>
          <FormControl fullWidth size="small" sx={{ flex: 1 }}>
            <InputLabel>Match</InputLabel>
            <Select label="Match" value={selectedMatchId} onChange={(event) => setSelectedMatchId(event.target.value)}>
              {matches.map((match) => (
                <MenuItem key={match.id} value={match.id}>{matchHeading(match)} {formatTime(match.match_time, timeFormat, match)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}>
            <TextField
              label="Date From"
              type="date"
              size="small"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1, minWidth: 160 }}
            />
            <Button size="small" onClick={() => setDateFrom('')} disabled={!dateFrom}>All Dates</Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              disabled={!previousMatch}
              onClick={() => previousMatch && setSelectedMatchId(previousMatch.id)}
              sx={{ minWidth: 170 }}
            >
              {previousMatch ? 'Previous Match' : 'No Previous Match'}
            </Button>
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              disabled={!nextMatch}
              onClick={() => nextMatch && setSelectedMatchId(nextMatch.id)}
              sx={{ minWidth: 150 }}
            >
              {nextMatch ? 'Next Match' : 'No Next Match'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {selectedMatch && (
        <Paper variant="outlined" sx={{ p: 2, borderLeft: `6px solid ${selectedMatch.match_site_type === 'Away' ? '#ed6c02' : '#2e7d32'}` }}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {selectedTeam?.team_name || lookupLabel(lookups, 'teams', selectedMatch.team_id)} vs {selectedMatch.opponent_name || 'TBD'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedMatch.match_site_type || 'Unknown'} · {formatMatchDate(localEventDate(selectedMatch))} · {formatTime(selectedMatch.match_time, timeFormat, selectedMatch) || 'Time TBD'} · {lookupLabel(lookups, 'facilities', selectedMatch.facility_id) || 'Facility TBD'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip color={readyCount >= requiredSlots ? 'success' : 'warning'} label={readinessText} sx={{ fontWeight: 800 }} />
                <Button variant="contained" disabled={!selectedMatch} onClick={() => selectedMatch && onOpenLineup?.(selectedMatch.id, { teamId })}>
                  Open Lineup Builder
                </Button>
              </Stack>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', lg: 'repeat(8, 1fr)' }, gap: 1 }}>
              {[
                ['Roster', players.length],
                ['Response %', players.length ? `${Math.round(((players.length - selectedMatchCounts.unknown) / players.length) * 100)}%` : '0%'],
                ['Available', selectedMatchCounts.available],
                ['Maybe', selectedMatchCounts.maybe],
                ['Unavailable', selectedMatchCounts.not_available],
                ['No Response', selectedMatchCounts.unknown],
                ['Required', requiredSlots || 'N/A'],
              ].map(([label, value]) => (
                <Paper key={String(label)} variant="outlined" sx={{ p: 1, bgcolor: label === 'Available' ? statusStyles.available.bg : label === 'Maybe' ? statusStyles.maybe.bg : label === 'Unavailable' ? statusStyles.not_available.bg : label === 'No Response' ? statusStyles.unknown.bg : 'background.paper' }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>{value}</Typography>
                </Paper>
              ))}
            </Box>
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={1.25}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ flex: 1, minWidth: 0, flexWrap: 'wrap' }} useFlexGap>
              <TextField size="small" label="Search players" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ minWidth: { sm: 240 } }} />
              {isMixedTeam && (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={genderFilter}
                  onChange={(_, value) => value && setGenderFilter(value)}
                  aria-label="Gender filter"
                  sx={{ flexWrap: 'wrap', gap: 0.5, '& .MuiToggleButtonGroup-grouped': { border: 1, borderColor: 'divider', borderRadius: '18px !important' } }}
                >
                  {genderFilterOptions.map((option) => <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>)}
                </ToggleButtonGroup>
              )}
            </Stack>
            <Button variant="outlined" startIcon={<MoreVertIcon />} onClick={(event) => setBulkAnchorEl(event.currentTarget)} disabled={!selectedMatch || readOnly}>
              Bulk Actions
            </Button>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ flexWrap: 'wrap' }} useFlexGap>
            <PlayerDisplayControls userPreferences={userPreferences} onChange={onUserPreferencesChange} showGroupBySex={false} variant="inline" />
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Sort</InputLabel>
              <Select label="Sort" value={availabilitySort} onChange={(event) => setAvailabilitySort(event.target.value as AvailabilitySort)}>
                <MenuItem value="no_response">No Response First</MenuItem>
                <MenuItem value="first_name">First Name</MenuItem>
                <MenuItem value="last_name">Last Name</MenuItem>
                <MenuItem value="availability">Availability Status</MenuItem>
                <MenuItem value="gender">Gender</MenuItem>
                <MenuItem value="last_updated">Last Updated</MenuItem>
              </Select>
            </FormControl>
            {isMixedTeam && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupByGender}
                    onChange={(event) => {
                      setGroupByGender(event.target.checked);
                      onUserPreferencesChange({ groupPlayersBySex: event.target.checked });
                    }}
                  />
                }
                label="Group by Gender"
                sx={{ mx: 0, whiteSpace: 'nowrap' }}
              />
            )}
            <ToggleButtonGroup
              exclusive
              size="small"
              value={availabilityFilter}
              onChange={(_, value) => value && setAvailabilityFilter(value)}
              aria-label="Availability filter"
              sx={{ flexWrap: 'wrap', gap: 0.5, '& .MuiToggleButtonGroup-grouped': { border: 1, borderColor: 'divider', borderRadius: '18px !important' } }}
            >
              {availabilityFilterOptions.map((option) => <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>)}
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Paper>

      {teams.length === 0 && <Alert severity="info">Create a team before marking availability.</Alert>}
      {teamId && players.length === 0 && !loading && <Alert severity="info">Add active roster players before marking availability.</Alert>}
      {teamId && matches.length === 0 && !loading && (
        <Alert severity="info">
          {dateFrom ? 'No matches on or after Date From. Choose an earlier date or All Dates to review past matches.' : 'Schedule matches for this team before marking availability.'}
        </Alert>
      )}

      {loading && <LinearProgress />}

      {selectedMatch && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) 360px' }, gap: 2 }}>
          <Stack spacing={1.5}>
            {noResponsePlayers.length > 0 ? (
              <Accordion defaultExpanded variant="outlined" disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Needs Attention</Typography>
                    <Chip size="small" color="warning" label={`${noResponsePlayers.length} No Response`} sx={{ fontWeight: 800 }} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>{renderPlayerGroup(noResponsePlayers)}</AccordionDetails>
              </Accordion>
            ) : (
              <Alert severity="success">All roster players have responded for this match.</Alert>
            )}

            {renderStatusSection('available', 'Available')}
            {renderStatusSection('maybe', 'Maybe')}
            {renderStatusSection('not_available', 'Unavailable')}
            {sortedFilteredPlayers.length === 0 && !loading && (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>No players match these filters</Typography>
                <Typography variant="body2" color="text.secondary">Try another status, gender, or search value.</Typography>
              </Paper>
            )}
          </Stack>

          <Stack spacing={1.5}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Lineup Readiness</Typography>
              <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.75 }}>{readinessText}</Typography>
              <Typography variant="body2" color="text.secondary">{readyCount} available · {requiredSlots || 'N/A'} required</Typography>
              <Button fullWidth variant="outlined" sx={{ mt: 1.25 }} onClick={() => selectedMatch && onOpenLineup?.(selectedMatch.id, { teamId })}>
                Open Lineup Builder
              </Button>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Still Waiting On</Typography>
              {noResponsePlayers.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>No players are waiting.</Typography>
              ) : (
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {noResponsePlayers.slice(0, 12).map((player) => (
                    <Stack key={player.id} direction="row" spacing={0.75} alignItems="center">
                      {renderGenderIcon(player)}
                      <Typography variant="body2">{formatPlayerName(player, userPreferences)}</Typography>
                    </Stack>
                  ))}
                  {noResponsePlayers.length > 12 && <Typography variant="caption" color="text.secondary">+{noResponsePlayers.length - 12} more</Typography>}
                </Stack>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Recent Updates</Typography>
              {groupMeUpdatesLoading ? (
                <LinearProgress sx={{ mt: 1 }} />
              ) : groupMeUpdates.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>No new GroupMe availability updates.</Typography>
              ) : (
                <Stack divider={<Divider />} sx={{ maxHeight: 190, overflowY: 'auto', mt: 0.75 }}>
                  {groupMeUpdates.map((update) => (
                    <Typography key={update.id} variant="body2" sx={{ py: 0.75, lineHeight: 1.35 }}>
                      {groupMeUpdateTimestamp(update.created_at, timeFormat)} — {update.player_name} is now <Box component="span" sx={{ fontWeight: 800 }}>{groupMeStatusLabel(update.new_status || 'unknown')}</Box> via GroupMe for {groupMeUpdateMatchLabel(update, timeFormat)}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Paper>
          </Stack>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Maybe counts as an available opportunity for future fairness calculations. Unknown does not count as an available opportunity.
          Not Available will block official lineup selection in later phases.
        </Typography>
      </Paper>

      <Menu anchorEl={bulkAnchorEl} open={Boolean(bulkAnchorEl)} onClose={() => setBulkAnchorEl(null)}>
        <MenuItem onClick={() => void updateMany(players, 'available', 'Set Everyone Available', true)}>
          Set Everyone Available
        </MenuItem>
        <MenuItem onClick={() => void updateMany(players, 'maybe', 'Set Everyone Maybe', true)}>
          Set Everyone Maybe
        </MenuItem>
        <MenuItem onClick={() => void updateMany(players, 'not_available', 'Set Everyone Unavailable', true)}>
          Set Everyone Unavailable
        </MenuItem>
        <MenuItem onClick={() => void updateMany(players.filter((player) => (selectedMatchRecords.get(player.id)?.availability_status || 'unknown') === 'unknown'), 'maybe', 'Set No Response Players to Maybe', false)}>
          Set No Response Players to Maybe — {selectedMatchCounts.unknown} player{selectedMatchCounts.unknown === 1 ? '' : 's'}
        </MenuItem>
        <MenuItem onClick={() => void clearAllResponses()}>
          Clear All Responses
        </MenuItem>
      </Menu>

      <Snackbar open={Boolean(snack)} autoHideDuration={4000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
